import { FC, useState, useMemo, useCallback } from "react";
import {
  Tag,
  Typography,
  Button,
  Select,
  message,
  Spin,
  Tooltip,
  Input,
  Drawer,
} from "antd";
import { CalendarOutlined, SaveOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useGetStaffAvailabilityDetails } from "~/services/operation";
import { StaffAvailability } from "~/utils/constants";
import dayjs from "dayjs";
import {
  useCreateStaffAvailabilityList,
  useUpdateStaffAvailabilityList,
} from "~/services/staff/staff-availability.queries";
import { cn } from "~/utils";

const { Text } = Typography;
const { TextArea } = Input;

interface Props {
  staffId: string;
  staffName: string;
  visible: boolean;
  onClose: () => void;
}

interface AvailabilityDetail {
  id?: string;
  date: Date;
  availability: StaffAvailability;
  note?: string;
  bookedHours?: number;
  availableHours?: number;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const StaffAvailabilityDetails: FC<Props> = ({
  staffId,
  staffName,
  visible,
  onClose,
}) => {
  // Track the starting week (start of week containing today)
  const [weekOffset, setWeekOffset] = useState(0);

  // Calculate the 2-week period starting from Sunday
  const { days, startDate, endDate } = useMemo(() => {
    // Get start of current week (Sunday)
    const baseWeekStart = dayjs().startOf("week").add(weekOffset, "week");
    const generatedDays: dayjs.Dayjs[] = [];

    // Generate 14 days (2 weeks)
    for (let i = 0; i < 14; i++) {
      generatedDays.push(baseWeekStart.add(i, "day"));
    }

    return {
      days: generatedDays,
      startDate: generatedDays[0].format("YYYY-MM-DD"),
      endDate: generatedDays[13].format("YYYY-MM-DD"),
    };
  }, [weekOffset]);

  // Track selected dates and form state
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [pendingAvailability, setPendingAvailability] = useState<StaffAvailability>(
    StaffAvailability.FULL_TIME
  );
  const [pendingNote, setPendingNote] = useState<string>("");

  // Fetch existing availability data
  const { data, isLoading, refetch } = useGetStaffAvailabilityDetails(
    staffId,
    startDate,
    endDate,
    visible
  );

  const createAvailabilityList = useCreateStaffAvailabilityList();
  const updateAvailabilityList = useUpdateStaffAvailabilityList();

  // Create a map of dates to availability details
  const availabilityMap = useMemo(() => {
    const map: Record<string, AvailabilityDetail> = {};
    if (data?.availabilityDetails) {
      data.availabilityDetails.forEach((detail: AvailabilityDetail) => {
        // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
        const dateStr = String(detail.date).split("T")[0];
        map[dateStr] = detail;
      });
    }
    return map;
  }, [data]);

  // Navigation handlers
  const goToPreviousWeeks = useCallback(() => {
    setWeekOffset((prev) => prev - 2);
    setSelectedDates(new Set());
  }, []);

  const goToNextWeeks = useCallback(() => {
    setWeekOffset((prev) => prev + 2);
    setSelectedDates(new Set());
  }, []);

  const goToToday = useCallback(() => {
    setWeekOffset(0);
    setSelectedDates(new Set());
  }, []);

  // Toggle date selection
  const toggleDate = useCallback((dateKey: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  }, []);

  // Bulk selection handlers
  const selectAll = useCallback(() => {
    setSelectedDates(new Set(days.map((d) => d.format("YYYY-MM-DD"))));
  }, [days]);

  const selectNone = useCallback(() => {
    setSelectedDates(new Set());
  }, []);

  const selectWeekdays = useCallback(() => {
    const weekdays = days
      .filter((d) => d.day() >= 1 && d.day() <= 5)
      .map((d) => d.format("YYYY-MM-DD"));
    setSelectedDates(new Set(weekdays));
  }, [days]);

  // Get color class for a date cell
  const getDateColor = useCallback(
    (dateKey: string, isSelected: boolean) => {
      if (isSelected) {
        return "ring-2 ring-blue-500 ring-offset-1";
      }

      const detail = availabilityMap[dateKey];
      if (!detail) {
        return "bg-gray-100 border-gray-200";
      }

      const bookedHours = Number(detail.bookedHours || 0);

      if (detail.availability === StaffAvailability.UNAVAILABLE) {
        return "bg-red-100 border-red-200";
      }

      if (bookedHours >= 7) {
        return "bg-red-100 border-red-200";
      }

      if (bookedHours >= 3) {
        return "bg-orange-100 border-orange-200";
      }

      if (detail.availability === StaffAvailability.PART_TIME) {
        return "bg-yellow-100 border-yellow-200";
      }

      return "bg-green-100 border-green-200";
    },
    [availabilityMap]
  );

  // Get status label for tooltip
  const getStatusLabel = useCallback(
    (dateKey: string) => {
      const detail = availabilityMap[dateKey];
      if (!detail) {
        return "No availability set";
      }

      const bookedHours = Number(detail.bookedHours || 0);
      let status = "";

      if (detail.availability === StaffAvailability.UNAVAILABLE) {
        status = "Unavailable";
      } else if (bookedHours >= 7) {
        status = `Fully Booked (${bookedHours.toFixed(1)}h)`;
      } else if (bookedHours >= 3) {
        status = `Partially Booked (${bookedHours.toFixed(1)}h)`;
      } else if (detail.availability === StaffAvailability.PART_TIME) {
        status = `Part Time (${bookedHours.toFixed(1)}h booked)`;
      } else {
        status = `Available (${bookedHours.toFixed(1)}h booked)`;
      }

      if (detail.note) {
        status += ` - ${detail.note}`;
      }

      return status;
    },
    [availabilityMap]
  );

  // Save availability for selected dates
  const handleSave = async () => {
    console.log("🔄 handleSave called", { selectedDates: Array.from(selectedDates), pendingAvailability, pendingNote });

    if (selectedDates.size === 0) {
      message.warning("Please select at least one date");
      return;
    }

    try {
      const datesToUpdate: { id: string; availability: StaffAvailability; note?: string }[] = [];
      const datesToCreate: { staffId: string; date: string; availability: StaffAvailability; note?: string }[] = [];

      selectedDates.forEach((dateKey) => {
        const existing = availabilityMap[dateKey];
        console.log(`  Date ${dateKey}: existing=`, existing);
        if (existing?.id) {
          datesToUpdate.push({
            id: existing.id,
            availability: pendingAvailability,
            note: pendingNote || undefined,
          });
        } else {
          datesToCreate.push({
            staffId,
            date: dateKey,
            availability: pendingAvailability,
            note: pendingNote || undefined,
          });
        }
      });

      console.log("📤 Sending:", { datesToUpdate, datesToCreate });

      const promises = [];

      if (datesToUpdate.length > 0) {
        promises.push(updateAvailabilityList.mutateAsync(datesToUpdate));
      }

      if (datesToCreate.length > 0) {
        promises.push(createAvailabilityList.mutateAsync(datesToCreate));
      }

      await Promise.all(promises);
      console.log("✅ Save successful");
      message.success(
        `Updated availability for ${selectedDates.size} date${selectedDates.size > 1 ? "s" : ""}`
      );
      setSelectedDates(new Set());
      setPendingNote("");
      refetch();
    } catch (error) {
      console.error("❌ Failed to save availability:", error);
      message.error("Failed to save availability");
    }
  };

  const isSaving = createAvailabilityList.isPending || updateAvailabilityList.isPending;

  // Check if today is in the current view
  const todayInView = days.some((d) => d.isSame(dayjs(), "day"));

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <CalendarOutlined className="text-blue-500" />
          <span>{staffName}'s Availability</span>
        </div>
      }
      placement="right"
      width={400}
      onClose={onClose}
      open={visible}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        <div className="space-y-4">
          {/* Legend */}
          <div className="flex flex-wrap gap-2 text-xs border-b pb-3">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-100 border border-green-200 rounded" />
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-100 border border-yellow-200 rounded" />
              <span>Part Time</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-100 border border-orange-200 rounded" />
              <span>Partial</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 border border-red-200 rounded" />
              <span>Off</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-100 border border-gray-200 rounded" />
              <span>Not Set</span>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              icon={<LeftOutlined />}
              onClick={goToPreviousWeeks}
              size="small"
            />
            <div className="flex flex-col items-center">
              <Text strong className="text-sm">
                {days[0].format("MMM D")} - {days[13].format("MMM D")}
              </Text>
              {!todayInView && (
                <Button size="small" type="link" onClick={goToToday} className="p-0 h-auto">
                  Back to Today
                </Button>
              )}
            </div>
            <Button
              icon={<RightOutlined />}
              onClick={goToNextWeeks}
              size="small"
            />
          </div>

          {/* Bulk Selection Buttons */}
          <div className="flex gap-1 flex-wrap">
            <Button size="small" onClick={selectAll}>
              All
            </Button>
            <Button size="small" onClick={selectNone}>
              Clear
            </Button>
            <Button size="small" onClick={selectWeekdays}>
              M-F
            </Button>
            {selectedDates.size > 0 && (
              <Tag color="blue" className="ml-auto">
                {selectedDates.size} selected
              </Tag>
            )}
          </div>

          {/* 2-Week Calendar Grid */}
          <div className="space-y-2">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  className="text-center text-[10px] font-semibold text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Week 1 */}
            <div className="grid grid-cols-7 gap-1">
              {days.slice(0, 7).map((day) => {
                const dateKey = day.format("YYYY-MM-DD");
                const isSelected = selectedDates.has(dateKey);
                const isToday = day.isSame(dayjs(), "day");
                const isWeekend = day.day() === 0 || day.day() === 6;
                const isPast = day.isBefore(dayjs(), "day");

                return (
                  <Tooltip key={dateKey} title={getStatusLabel(dateKey)}>
                    <button
                      type="button"
                      onClick={() => toggleDate(dateKey)}
                      className={cn(
                        "p-1.5 rounded border text-center cursor-pointer transition-all hover:scale-105",
                        getDateColor(dateKey, isSelected),
                        isToday && "ring-2 ring-blue-400",
                        isWeekend && "opacity-80",
                        isPast && "opacity-60"
                      )}
                    >
                      <div className="text-sm font-semibold">{day.format("D")}</div>
                      {isToday && (
                        <div className="text-[8px] text-blue-600 font-medium leading-tight">Today</div>
                      )}
                    </button>
                  </Tooltip>
                );
              })}
            </div>

            {/* Week 2 */}
            <div className="grid grid-cols-7 gap-1">
              {days.slice(7, 14).map((day) => {
                const dateKey = day.format("YYYY-MM-DD");
                const isSelected = selectedDates.has(dateKey);
                const isToday = day.isSame(dayjs(), "day");
                const isWeekend = day.day() === 0 || day.day() === 6;
                const isPast = day.isBefore(dayjs(), "day");

                return (
                  <Tooltip key={dateKey} title={getStatusLabel(dateKey)}>
                    <button
                      type="button"
                      onClick={() => toggleDate(dateKey)}
                      className={cn(
                        "p-1.5 rounded border text-center cursor-pointer transition-all hover:scale-105",
                        getDateColor(dateKey, isSelected),
                        isToday && "ring-2 ring-blue-400",
                        isWeekend && "opacity-80",
                        isPast && "opacity-60"
                      )}
                    >
                      <div className="text-sm font-semibold">{day.format("D")}</div>
                      {isToday && (
                        <div className="text-[8px] text-blue-600 font-medium leading-tight">Today</div>
                      )}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Set Availability Section */}
          {selectedDates.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <Text strong className="text-sm">
                Set availability for {selectedDates.size} date
                {selectedDates.size > 1 ? "s" : ""}:
              </Text>

              <Select
                value={pendingAvailability}
                onChange={setPendingAvailability}
                className="w-full"
                size="small"
                options={[
                  { value: StaffAvailability.FULL_TIME, label: "Available (Full Day)" },
                  { value: StaffAvailability.PART_TIME, label: "Part Time" },
                  { value: StaffAvailability.UNAVAILABLE, label: "Unavailable" },
                ]}
              />

              <TextArea
                value={pendingNote}
                onChange={(e) => setPendingNote(e.target.value)}
                placeholder="Add a note (optional)"
                rows={2}
                className="w-full"
                size="small"
              />

              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={isSaving}
                block
                size="small"
              >
                Save
              </Button>
            </div>
          )}

          {/* Empty state */}
          {selectedDates.size === 0 && (
            <div className="text-center text-gray-400 py-2 text-sm">
              Click dates to select
            </div>
          )}
        </div>
      </Spin>
    </Drawer>
  );
};
